import { inject, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatOptionSelectionChange } from '@angular/material/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { combineLatest, combineLatestWith, debounceTime, filter, Observable, startWith, Subscription, switchMap, tap } from 'rxjs';
import { FormType } from 'src/app/shared/interfaces/form-type';
import { AddressChoice, CoordinatesDto } from 'src/app/api/v1/models';
import { CitiesService } from 'src/app/services/cities.service';
import { FormStepAbstract } from '../../../../../shared/components/abstract/form-step.abstract';
import { SessionRepository } from '../../../../../shared/repositories/session.repository';
import { FormSaveStrategy } from '../../../strategy/form-save-strategy/form-save-strategy.abstract';
import { AddressSelectorService } from './address-selector.service';

interface IIndexAddressControl {
  postCode: string | null;
  street: string | null;
  house: string | null;
  flat: string | null;
  city: string | null;
}

@UntilDestroy()
export class IndexAddressStepAbstract extends FormStepAbstract implements OnInit {
  private cities: CitiesService = inject(CitiesService);
  private sessionRepository: SessionRepository = inject(SessionRepository);
  private addressSelectorService: AddressSelectorService = inject(AddressSelectorService);
  private formSaveStrategy: FormSaveStrategy = inject(FormSaveStrategy);

  private isInitPostcode: boolean = true;

  override form: FormGroup<FormType<IIndexAddressControl>> = new FormGroup<FormType<IIndexAddressControl>>({
    postCode: new FormControl<string | null>(null, [Validators.required]),
    street: new FormControl<string | null>(null, [Validators.required]),
    house: new FormControl<string | null>(null),
    flat: new FormControl<string | null>(null),
    city: new FormControl<string | null>(null),
  });

  streetControl: FormControl<string> = new FormControl<string>('', {nonNullable: true});

  private selectedAddressCoordinates: CoordinatesDto;

  isLoadingCities$: Observable<boolean> = this.cities.isLoading$;

  postcodeAddress$: Observable<AddressChoice[] | null> = this.cities.postcodeAddress$;

  override ngOnInit(): void {
    super.ngOnInit();

    this.subscribeToDeliveryAddress();

    this.form.controls.postCode.valueChanges.pipe(
      debounceTime(650),
      untilDestroyed(this),
      filter((postcode: string | null): postcode is string => !!postcode)
    ).subscribe((postcode: string) => {
      this.onChangePostcode(postcode);
    });

    combineLatest([
      this.streetControl.valueChanges.pipe(startWith(this.form.controls.street.getRawValue())),
      this.form.controls.flat.valueChanges.pipe(startWith(this.form.controls.flat.getRawValue()))
    ]).pipe(
      debounceTime(this.formSaveStrategy.debounceTimeout),
      filter(() => !!this.form.controls.street.getRawValue()),
      switchMap(() => this.formSaveStrategy.setDeliveryAddress(this.form.getRawValue(), this.selectedAddressCoordinates)),
      tap(() => {
        this.cities.setDeliveryCoordinates(this.selectedAddressCoordinates);
      }),
      untilDestroyed(this),
    ).subscribe();
  }

  private addressSub: Subscription;

  private subscribeToDeliveryAddress(): void {
    this.addressSub?.unsubscribe();
    this.addressSub = this.addressSelectorService.deliveryAddress$.pipe(
      combineLatestWith(this.sessionRepository.deliveryCoordinates$),
      tap(([deliveryAddress, coordinates]) => {
        if(!deliveryAddress || !coordinates) {
          return;
        }

        this.form.controls.postCode.setValue(deliveryAddress.postCode || '', {emitEvent: this.isInitPostcode});
        this.form.controls.city.setValue(deliveryAddress.city || '', {emitEvent: false});
        this.form.controls.street.setValue(deliveryAddress.street || '', {emitEvent: false});
        this.form.controls.flat.setValue(deliveryAddress.flat || '', {emitEvent: false});
        this.form.controls.house.setValue(deliveryAddress.house || '', {emitEvent: false});

        this.streetControl.setValue(`${deliveryAddress.house}, ${deliveryAddress.street}, ${deliveryAddress.city}`, {emitEvent: false});

        this.selectedAddressCoordinates = coordinates;

        this.isInitPostcode = false;
      }),
      untilDestroyed(this)
    ).subscribe();
  }

  onChangePostcode(postcode: string): void {
    if(postcode.length < 5 || postcode.length > 10) {
      return;
    }
    this.cities.loadAddressByPostcode(postcode);
  }

  onChangeAddress(address: AddressChoice, $event: MatOptionSelectionChange): void {
    if(!$event.isUserInput) {
      return;
    }

    this.form.controls.street.setValue(address.street);
    this.form.controls.house.setValue(address.building);
    this.form.controls.city.setValue(address.city);

    this.selectedAddressCoordinates = {
      lat: address.latitude,
      lng: address.longitude,
    };
  }
}
